/*
  ================================================================
  FLIPSIDE GAMING — SINGLES SEARCH
  ================================================================

  Handles:
  - Game-specific Singles search
  - All Singles search
  - Game selector
  - Predictive suggestions
  - Search-result routing
  - Keyboard navigation
  - Relevance-ranked autocomplete
  - Hybrid predictive-search fallback
  - Game labels on All Singles suggestions

  Supported product types:
  - magic-card
  - pokemon-card
  - onepiece-card
  - lorcana-card
  - gundam-card
  - riftbound-card

  ================================================================
*/

(function() {
  "use strict";


  // =========================================================
  // SEARCH CONSTANTS
  // =========================================================

  var ALL_SINGLES_TYPE =
    "all-singles";


  var SINGLES_PRODUCT_TYPES = [
    "magic-card",
    "pokemon-card",
    "onepiece-card",
    "lorcana-card",
    "gundam-card",
    "riftbound-card",
    "graded-slab"
  ];


  var SUGGESTION_LIMIT =
    8;


  /*
    If the first Shopify request already gives
    us this many good suggestions, don't make
    fallback requests.
  */
  var SUGGESTION_FALLBACK_THRESHOLD =
    6;



  // =========================================================
  // INITIALIZE ONE SEARCH BAR
  // =========================================================

  function initializeSinglesSearch(
    searchRoot
  ) {

    if (
      !searchRoot ||
      searchRoot.dataset.searchInitialized ===
        "true"
    ) {
      return;
    }


    searchRoot.dataset.searchInitialized =
      "true";


    var input =
      searchRoot.querySelector(
        ".singles-search-input"
      );


    var suggestionsBox =
      searchRoot.querySelector(
        ".singles-search-suggestions"
      );


    var form =
      searchRoot.closest(
        "form"
      );


    var gameSelect =
      searchRoot.querySelector(
        "[data-singles-game-select]"
      );


    if (
      !input ||
      !suggestionsBox
    ) {
      return;
    }


    var suggestionTimer =
      null;


    var suggestionRequest =
      null;


    var suggestionItems =
      [];


    var highlightedSuggestion =
      -1;



    // =======================================================
    // SEARCH CONFIG
    // =======================================================

    function getSelectedGameOption() {

      if (
        !gameSelect ||
        !gameSelect.options ||
        gameSelect.selectedIndex < 0
      ) {
        return null;
      }


      return gameSelect.options[
        gameSelect.selectedIndex
      ];
    }


    function getGameType() {

      var selectedOption =
        getSelectedGameOption();


      if (
        selectedOption
      ) {

        var selectedGameType =
          selectedOption.getAttribute(
            "data-game-type"
          ) ||
          selectedOption.value;


        if (
          selectedGameType
        ) {
          return String(
            selectedGameType
          )
            .trim()
            .toLowerCase();
        }
      }


      return String(
        searchRoot.getAttribute(
          "data-game-type"
        ) ||
        "magic-card"
      )
        .trim()
        .toLowerCase();
    }


    function getSearchView() {

      var selectedOption =
        getSelectedGameOption();


      if (
        selectedOption
      ) {

        var selectedView =
          selectedOption.getAttribute(
            "data-search-view"
          );


        if (
          selectedView
        ) {
          return String(
            selectedView
          ).trim();
        }
      }


      return String(
        searchRoot.getAttribute(
          "data-search-view"
        ) ||
        "magic-singles"
      ).trim();
    }


    function isSinglesProductType(
      productType
    ) {

      return (
        SINGLES_PRODUCT_TYPES.indexOf(
          String(
            productType || ""
          )
            .trim()
            .toLowerCase()
        ) !== -1
      );
    }


    function getSinglesGameLabel(
      productType
    ) {

      switch (
        String(
          productType || ""
        )
          .trim()
          .toLowerCase()
      ) {

        case "magic-card":
          return "Magic";

        case "pokemon-card":
          return "Pokémon";

        case "onepiece-card":
          return "One Piece";

        case "lorcana-card":
          return "Lorcana";

        case "gundam-card":
          return "Gundam";

        case "riftbound-card":
          return "Riftbound";

        case "graded-slab":
          return "Graded Cards";  

        default:
          return "";
      }
    }



    // =======================================================
    // TEXT HELPERS
    // =======================================================

    function cleanSearchQuery(
      value
    ) {

      return String(
        value || ""
      )
        .replace(
          /\s*product_{1,2}type\s*:\s*["']?[^"'\s()]+["']?/gi,
          ""
        )

        .replace(
          /\btitle:/gi,
          ""
        )

        .replace(
          /\s+OR\s+/gi,
          " "
        )

        .replace(
          /[()]/g,
          " "
        )

        .replace(
          /\s+/g,
          " "
        )

        .trim();
    }


    function escapeHtml(
      value
    ) {

      return String(
        value || ""
      )
        .replace(
          /&/g,
          "&amp;"
        )

        .replace(
          /</g,
          "&lt;"
        )

        .replace(
          />/g,
          "&gt;"
        )

        .replace(
          /"/g,
          "&quot;"
        )

        .replace(
          /'/g,
          "&#039;"
        );
    }


    function cleanSuggestedCardName(
      title
    ) {

      return String(
        title || ""
      )
        .replace(
          /\s*\([^)]*\)\s*$/,
          ""
        )

        .replace(
          /\s+/g,
          " "
        )

        .trim();
    }


    function normalizeSuggestionText(
      value
    ) {

      var text =
        String(
          value || ""
        );


      /*
        Remove accent marks for matching,
        while leaving the visible title alone.
      */
      if (
        text.normalize
      ) {

        text =
          text
            .normalize(
              "NFD"
            )

            .replace(
              /[\u0300-\u036f]/g,
              ""
            );
      }


      return text

        .toLowerCase()

        /*
          MTG split cards.
        */
        .replace(
          /\s*\/\/\s*/g,
          " "
        )

        /*
          Spaced title separators.
        */
        .replace(
          /\s+[-–—]\s+/g,
          " "
        )

        /*
          Remaining punctuation becomes spaces.
        */
        .replace(
          /[^a-z0-9]+/g,
          " "
        )

        .replace(
          /\s+/g,
          " "
        )

        .trim();
    }



    // =======================================================
    // RESULTS-PAGE QUERY BUILDING
    // =======================================================

    function buildTitleQuery(
      query
    ) {
      /*
        Convert customer-entered card names into safe
        Shopify title search terms.

        Examples:

          Linvala, Keeper of Silence
            ->
          title:Linvala title:Keeper title:Silence

          Aang's Iceberg
            ->
          title:Aang title:Iceberg

          Aang’s Iceberg
            ->
          title:Aang title:Iceberg

          Fire // Ice
            ->
          title:Fire title:Ice

          Rock & Roll
            ->
          title:Rock title:Roll

          Mushu - Sneaky Dragon
            ->
          title:Mushu title:Sneaky title:Dragon

          Spider-Man
            ->
          title:Spider-Man
      */


      var ignoredWords = [
        "a",
        "an",
        "and",
        "by",
        "of",
        "or",
        "the"
      ];

      var writtenNumberWords = [
        "zero",
        "one",
        "two",
        "three",
        "four",
        "five",
        "six",
        "seven",
        "eight",
        "nine",
        "ten",
        "eleven",
        "twelve",
        "thirteen",
        "fourteen",
        "fifteen",
        "sixteen",
        "seventeen",
        "eighteen",
        "nineteen",
        "twenty",
        "thirty",
        "forty",
        "fifty",
        "sixty",
        "seventy",
        "eighty",
        "ninety",
        "hundred",
        "thousand"
      ];


      var normalizedQuery =
        String(
          query || ""
        )

          /*
            Normalize smart apostrophes first.
          */
          .replace(
            /[’‘]/g,
            "'"
          )

          /*
            Remove possessive endings.

            Aang's -> Aang
            Urza's -> Urza
          */
          .replace(
            /'s\b/gi,
            ""
          )

          /*
            Remove remaining apostrophes inside words.

            This prevents them from creating isolated
            one-letter search terms.
          */
          .replace(
            /'/g,
            ""
          )

          /*
            MTG split-card separator.
          */
          .replace(
            /\s*\/\/\s*/g,
            " "
          )

          /*
            Ampersands are separators for search.

            Rock & Roll -> Rock Roll
          */
          .replace(
            /&/g,
            " "
          )

          /*
            Remove spaced dash separators while
            preserving internal hyphens.

            Mushu - Sneaky Dragon -> Mushu Sneaky Dragon
            Spider-Man -> Spider-Man
          */
          .replace(
            /\s+[-–—]\s+/g,
            " "
          )

          /*
            Everything else that isn't a letter,
            number, whitespace, or internal hyphen
            becomes a space.

            Covers things like:

              ,
              .
              :
              ;
              !
              ?
              "
              (
              )
              [
              ]
              {
              }
              +
              =
              *
              #
              @
              %
              etc.
          */
          .replace(
            /[^A-Za-z0-9\u00C0-\u024F\s-]+/g,
            " "
          )

          .replace(
            /\s+/g,
            " "
          )

          .trim();


      var words =
        normalizedQuery

          .split(
            /\s+/
          )

          .filter(
            Boolean
          );


      /*
        Remove ordinary connector words first.

        Examples:
          Linvala, Keeper of Silence
            -> Linvala Keeper Silence

          The One Ring
            -> One Ring
      */
      /*
        First remove common connector words.
      */
      var meaningfulWords =
        words.filter(function(
          word
        ) {

          return (
            ignoredWords.indexOf(
              word.toLowerCase()
            ) === -1
          );

        });


      /*
        Then remove written-out numbers from the words
        that Shopify will be REQUIRED to match.

        Examples:

          Sheoldred, Whispering One
            -> Sheoldred Whispering

          The One Ring
            -> Ring
      */
      var searchableWords =
        meaningfulWords.filter(function(
          word
        ) {

          return (
            writtenNumberWords.indexOf(
              word.toLowerCase()
            ) === -1
          );

        });


      /*
        If removing written-out numbers still leaves
        at least two strong title words, use those
        as required Shopify title terms.

        Example:

          Sheoldred, Whispering One
            ->
          title:Sheoldred title:Whispering
      */
      if (
        searchableWords.length >= 2
      ) {

        words =
          searchableWords;

      } else {

        /*
          If only one strong word (or none) remains,
          searching that word alone would be far too broad.

          Use the complete card name as a quoted title
          phrase instead.

          Examples:

            The One Ring
              ->
            title:"The One Ring"

            Two by Four
              ->
            title:"Two by Four"

            Seven Dwarves
              ->
            title:"Seven Dwarves"
        */
        return (
          'title:"' +
          normalizedQuery +
          '"'
        );
      }

      return words

        .map(function(
          word
        ) {
          return (
            "title:" +
            word
          );
        })

        .join(
          " "
        );
    }


    function buildAllSinglesTypeQuery() {

      return (
        "(" +

        SINGLES_PRODUCT_TYPES

          .map(function(
            productType
          ) {

            return (
              "product_type:" +
              productType
            );

          })

          .join(
            " OR "
          ) +

        ")"
      );
    }


    function buildResultsQuery(
      query
    ) {

      var titleQuery =
        buildTitleQuery(
          query
        );


      var gameType =
        getGameType();


      /*
        ALL SINGLES
      */
      if (
        gameType ===
        ALL_SINGLES_TYPE
      ) {

        return (
          titleQuery +
          " " +
          buildAllSinglesTypeQuery()
        );
      }


      /*
        INDIVIDUAL GAME
      */
      return (
        titleQuery +
        " product_type:" +
        gameType
      );
    }



    // =======================================================
    // PREDICTIVE QUERY BUILDING
    // =======================================================

    function getPredictiveGameTerms(
      productType
    ) {

      return String(
        productType || ""
      )
        .replace(
          /[-_]+/g,
          " "
        )

        .replace(
          /\s+/g,
          " "
        )

        .trim();
    }


    function buildPredictiveQuery(
      query,
      productType,
      includeGameTerms
    ) {

      var cleanQuery =
        String(
          query || ""
        ).trim();


      /*
        PRIMARY REQUEST

        Search exactly what the customer typed.

        "pi"

        stays:

        "pi"

        rather than becoming:

        "pokemon card pi"
      */
      if (
        !includeGameTerms ||
        !productType ||
        productType ===
          ALL_SINGLES_TYPE
      ) {

        return cleanQuery;
      }


      /*
        FALLBACK REQUEST

        Example:

        pikachu pokemon card
      */
      return [
        cleanQuery,

        getPredictiveGameTerms(
          productType
        )
      ]

        .filter(
          Boolean
        )

        .join(
          " "
        );
    }



    // =======================================================
    // SUGGESTION RANKING
    // =======================================================

    function getSuggestionRank(
      cardName,
      query
    ) {

      var normalizedName =
        normalizeSuggestionText(
          cardName
        );


      var normalizedQuery =
        normalizeSuggestionText(
          query
        );


      if (
        !normalizedName ||
        !normalizedQuery
      ) {

        return 999;
      }


      /*
        0 — exact title

        Pikachu
        search: Pikachu
      */
      if (
        normalizedName ===
        normalizedQuery
      ) {

        return 0;
      }


      /*
        1 — title begins with query

        Pikachu V
        Pikachu ex
        Pikachu 58/102
      */
      if (
        normalizedName.indexOf(
          normalizedQuery
        ) === 0
      ) {

        return 1;
      }


      var nameWords =
        normalizedName

          .split(
            " "
          )

          .filter(
            Boolean
          );


      /*
        2 — later word begins with query

        Flying Pikachu V
        search: Pikachu
      */
      var laterWordStartsWithQuery =
        nameWords.some(function(
          word
        ) {

          return (
            word.indexOf(
              normalizedQuery
            ) === 0
          );

        });


      if (
        laterWordStartsWithQuery
      ) {

        return 2;
      }


      /*
        3 — query exists elsewhere.

        Gulpin
        search: pi
      */
      if (
        normalizedName.indexOf(
          normalizedQuery
        ) !== -1
      ) {

        return 3;
      }


      /*
        4 — multi-word search where all query
        words appear somewhere within the title.

        mushu dragon
          ->
        Mushu - Sneaky Dragon
      */
      var queryWords =
        normalizedQuery

          .split(
            " "
          )

          .filter(
            Boolean
          );


      if (
        queryWords.length > 1
      ) {

        var allWordsPresent =
          queryWords.every(function(
            queryWord
          ) {

            return nameWords.some(function(
              nameWord
            ) {

              return (
                nameWord.indexOf(
                  queryWord
                ) !== -1
              );

            });

          });


        if (
          allWordsPresent
        ) {

          return 4;
        }
      }


      /*
        Anything else is unrelated enough
        that we don't want it in autocomplete.
      */
      return 999;
    }



    // =======================================================
    // BUILD RANKED SUGGESTION OBJECTS
    // =======================================================

    function buildRankedSuggestions(
      products,
      query,
      gameType
    ) {

      var uniqueNames =
        {};


      var rankedSuggestions =
        [];


      (
        products || []
      ).forEach(function(
        product,
        originalIndex
      ) {

        if (
          !product
        ) {
          return;
        }


        var productType =
          String(
            product.type ||
            product.product_type ||
            ""
          )
            .trim()
            .toLowerCase();


        /*
          ALL SINGLES

          Must be one of our six supported
          Singles product types.
        */
        if (
          gameType ===
          ALL_SINGLES_TYPE
        ) {

          if (
            !isSinglesProductType(
              productType
            )
          ) {

            return;
          }

        } else {

          /*
            INDIVIDUAL GAME

            Require the selected product type
            when Shopify supplies one.
          */
          if (
            productType &&
            productType !==
              gameType
          ) {

            return;
          }
        }


        var cleanName =
          cleanSuggestedCardName(
            product.title
          );


        if (
          !cleanName
        ) {
          return;
        }


        var key =
          normalizeSuggestionText(
            cleanName
          );


        if (
          !key ||
          uniqueNames[
            key
          ]
        ) {

          return;
        }


        var rank =
          getSuggestionRank(
            cleanName,
            query
          );


        /*
          Shopify sometimes returns products that
          don't meaningfully contain the customer's
          search text.

          Don't display those.
        */
        if (
          rank >= 999
        ) {

          return;
        }


        uniqueNames[
          key
        ] =
          true;


        rankedSuggestions.push({
          name:
            cleanName,

          productType:
            productType,

          gameLabel:
            getSinglesGameLabel(
              productType
            ),

          rank:
            rank,

          originalIndex:
            originalIndex
        });

      });


      rankedSuggestions.sort(function(
        a,
        b
      ) {

        /*
          Our relevance ranking first.
        */
        if (
          a.rank !==
          b.rank
        ) {

          return (
            a.rank -
            b.rank
          );
        }


        /*
          Shopify's original ordering remains
          the tiebreaker.
        */
        return (
          a.originalIndex -
          b.originalIndex
        );

      });


      return rankedSuggestions;
    }



    // =======================================================
    // SUGGESTION STATE
    // =======================================================

    function closeSuggestions() {

      if (
        suggestionRequest
      ) {

        suggestionRequest.abort();


        suggestionRequest =
          null;
      }


      suggestionsBox.hidden =
        true;


      suggestionsBox.innerHTML =
        "";


      suggestionItems =
        [];


      highlightedSuggestion =
        -1;


      input.setAttribute(
        "aria-expanded",
        "false"
      );
    }


    function updateSuggestionHighlight() {

      suggestionsBox

        .querySelectorAll(
          ".singles-search-suggestion"
        )

        .forEach(function(
          button,
          index
        ) {

          button.classList.toggle(
            "is-highlighted",

            index ===
              highlightedSuggestion
          );

        });
    }



    // =======================================================
    // SUBMIT SEARCH
    // =======================================================

    function submitSearch(
      value
    ) {

      var query =
        cleanSearchQuery(
          value
        );


      if (
        !query
      ) {

        input.focus();

        return;
      }


      closeSuggestions();


      var finalQuery =
        buildResultsQuery(
          query
        );


      window.location.href =
        "/search" +

        "?type=product" +

        "&view=" +
        encodeURIComponent(
          getSearchView()
        ) +

        "&q=" +
        encodeURIComponent(
          finalQuery
        ) +

        "&singles_query=" +
        encodeURIComponent(
          query
        );
    }


    function submitSuggestion(
      item
    ) {

      if (
        !item ||
        !item.name
      ) {
        return;
      }


      input.value =
        item.name;


      submitSearch(
        item.name
      );
    }



    // =======================================================
    // RENDER PREDICTIVE RESULTS
    // =======================================================

    function renderSuggestions(
      products,
      query,
      gameType
    ) {

      var resolvedGameType =
        gameType ||
        getGameType();


      var allSingles =
        resolvedGameType ===
        ALL_SINGLES_TYPE;


      suggestionItems =
        buildRankedSuggestions(
          products,
          query,
          resolvedGameType
        )

          .slice(
            0,
            SUGGESTION_LIMIT
          );


      highlightedSuggestion =
        -1;


      if (
        !suggestionItems.length
      ) {

        closeSuggestions();

        return;
      }


      suggestionsBox.innerHTML =
        suggestionItems

          .map(function(
            item,
            index
          ) {

            var gameLabelHtml =
              "";


            /*
              Only All Singles displays game labels.

              Individual game searches remain clean.
            */
            if (
              allSingles &&
              item.gameLabel
            ) {

              gameLabelHtml =
                "<span class=\"singles-search-suggestion-game\">" +

                  escapeHtml(
                    item.gameLabel
                  ) +

                "</span>";
            }


            return (
              "<button" +

                " type=\"button\"" +

                " class=\"singles-search-suggestion\"" +

                " role=\"option\"" +

                " data-suggestion-index=\"" +
                  index +
                "\"" +

              ">" +

                "<span class=\"singles-search-suggestion-name\">" +

                  escapeHtml(
                    item.name
                  ) +

                "</span>" +

                gameLabelHtml +

              "</button>"
            );

          })

          .join(
            ""
          );


      suggestionsBox.hidden =
        false;


      input.setAttribute(
        "aria-expanded",
        "true"
      );


      suggestionsBox

        .querySelectorAll(
          ".singles-search-suggestion"
        )

        .forEach(function(
          button
        ) {

          button.addEventListener(
            "mousedown",

            function(
              event
            ) {

              event.preventDefault();


              var index =
                Number(
                  button.getAttribute(
                    "data-suggestion-index"
                  )
                );


              var item =
                suggestionItems[
                  index
                ];


              if (
                item
              ) {

                submitSuggestion(
                  item
                );
              }

            }
          );

        });
    }



    // =======================================================
    // PREDICTIVE REQUEST HELPERS
    // =======================================================

    function getShopRoot() {

      return (
        window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root
      )
        ? window.Shopify.routes.root
        : "/";
    }


    function requestPredictiveProducts(
      predictiveQuery,
      signal
    ) {

      var url =
        getShopRoot() +

        "search/suggest.json" +

        "?q=" +
        encodeURIComponent(
          predictiveQuery
        ) +

        "&resources[type]=product" +

        "&resources[limit]=10" +

        "&resources[options][fields]=title,product_type" +

        "&resources[options][unavailable_products]=show";


      return fetch(
        url,
        {
          signal:
            signal,

          headers: {
            Accept:
              "application/json"
          }
        }
      )

        .then(function(
          response
        ) {

          if (
            !response.ok
          ) {

            throw new Error(
              "Predictive search failed: " +
              response.status
            );
          }


          return response.json();

        })

        .then(function(
          data
        ) {

          return (
            data &&
            data.resources &&
            data.resources.results &&
            Array.isArray(
              data.resources
                .results
                .products
            )
          )
            ? data.resources
                .results
                .products
            : [];

        });
    }


    function requestFallbackProducts(
      predictiveQuery,
      signal
    ) {

      return requestPredictiveProducts(
        predictiveQuery,
        signal
      )

        .catch(function(
          error
        ) {

          /*
            An abort must propagate so an older
            autocomplete cannot render after a
            newer search.
          */
          if (
            error.name ===
            "AbortError"
          ) {

            throw error;
          }


          /*
            One failed fallback shouldn't break
            the whole autocomplete.
          */
          console.warn(
            "Singles fallback predictive search failed:",
            predictiveQuery,
            error
          );


          return [];

        });
    }


    function mergeProductLists(
      productLists
    ) {

      var merged =
        [];


      (
        productLists || []
      ).forEach(function(
        products
      ) {

        (
          products || []
        ).forEach(function(
          product
        ) {

          merged.push(
            product
          );

        });

      });


      return merged;
    }



    // =======================================================
    // FETCH PREDICTIVE RESULTS
    // =======================================================

    function fetchSuggestions(
      query
    ) {

      /*
        Cancel the previous autocomplete request
        if the customer continues typing.
      */
      if (
        suggestionRequest
      ) {

        suggestionRequest.abort();
      }


      var controller =
        new AbortController();


      suggestionRequest =
        controller;


      /*
        Capture the selected game now.

        Changing the dropdown will abort this
        controller through closeSuggestions().
      */
      var activeGameType =
        getGameType();


      /*
        =====================================================
        PRIMARY REQUEST
        =====================================================

        Always begin with exactly what the
        customer typed.

        "pi" stays "pi".
      */
      var primaryQuery =
        buildPredictiveQuery(
          query,
          activeGameType,
          false
        );


      requestPredictiveProducts(
        primaryQuery,
        controller.signal
      )

        .then(function(
          primaryProducts
        ) {

          if (
            controller.signal.aborted
          ) {

            return;
          }


          var primarySuggestions =
            buildRankedSuggestions(
              primaryProducts,
              query,
              activeGameType
            );


          /*
            Shopify already gave us enough good
            suggestions.

            No fallback necessary.
          */
          if (
            primarySuggestions.length >=
            SUGGESTION_FALLBACK_THRESHOLD
          ) {

            renderSuggestions(
              primaryProducts,
              query,
              activeGameType
            );


            return;
          }


          // =================================================
          // ALL SINGLES — HYBRID FALLBACK
          // =================================================

          if (
            activeGameType ===
            ALL_SINGLES_TYPE
          ) {

            /*
              The broad search wasn't good enough.

              Ask each supported game for more
              candidates, then merge, filter,
              deduplicate and rank everything.
            */

            var fallbackRequests =
              SINGLES_PRODUCT_TYPES

                .map(function(
                  productType
                ) {

                  var fallbackQuery =
                    buildPredictiveQuery(
                      query,
                      productType,
                      true
                    );


                  return requestFallbackProducts(
                    fallbackQuery,
                    controller.signal
                  );

                });


            return Promise

              .all(
                fallbackRequests
              )

              .then(function(
                fallbackProductLists
              ) {

                if (
                  controller.signal.aborted
                ) {

                  return;
                }


                var mergedProducts =
                  mergeProductLists(
                    [
                      primaryProducts
                    ].concat(
                      fallbackProductLists
                    )
                  );


                renderSuggestions(
                  mergedProducts,
                  query,
                  activeGameType
                );

              });
          }


          // =================================================
          // INDIVIDUAL GAME — FALLBACK
          // =================================================

          /*
            Raw customer query didn't provide enough
            matching cards.

            Make one additional game-assisted request.

            Example:

            Primary:
              pikachu

            Fallback:
              pikachu pokemon card

            The relevance filter still requires the
            visible title to actually match Pikachu.
          */

          var fallbackQuery =
            buildPredictiveQuery(
              query,
              activeGameType,
              true
            );


          return requestFallbackProducts(
            fallbackQuery,
            controller.signal
          )

            .then(function(
              fallbackProducts
            ) {

              if (
                controller.signal.aborted
              ) {

                return;
              }


              renderSuggestions(
                primaryProducts.concat(
                  fallbackProducts
                ),
                query,
                activeGameType
              );

            });

        })

        .catch(function(
          error
        ) {

          if (
            error.name ===
            "AbortError"
          ) {

            return;
          }


          console.error(
            "Unable to load singles suggestions:",
            error
          );


          closeSuggestions();

        });
    }



    // =======================================================
    // INPUT EVENTS
    // =======================================================

    input.addEventListener(
      "input",

      function() {

        window.clearTimeout(
          suggestionTimer
        );


        var query =
          cleanSearchQuery(
            input.value
          );


        /*
          Don't run autocomplete for a single
          character.
        */
        if (
          query.length < 2
        ) {

          closeSuggestions();

          return;
        }


        suggestionTimer =
          window.setTimeout(
            function() {

              fetchSuggestions(
                query
              );

            },

            220
          );

      }
    );



    // =======================================================
    // KEYBOARD CONTROLS
    // =======================================================

    input.addEventListener(
      "keydown",

      function(
        event
      ) {

        if (
          event.key ===
            "ArrowDown" &&
          suggestionItems.length
        ) {

          event.preventDefault();


          highlightedSuggestion =
            (
              highlightedSuggestion +
              1
            ) %
            suggestionItems.length;


          updateSuggestionHighlight();


          return;
        }


        if (
          event.key ===
            "ArrowUp" &&
          suggestionItems.length
        ) {

          event.preventDefault();


          highlightedSuggestion =
            highlightedSuggestion <=
              0

              ? suggestionItems.length -
                1

              : highlightedSuggestion -
                1;


          updateSuggestionHighlight();


          return;
        }


        if (
          event.key ===
            "Enter" &&
          highlightedSuggestion >=
            0
        ) {

          event.preventDefault();


          submitSuggestion(
            suggestionItems[
              highlightedSuggestion
            ]
          );


          return;
        }


        if (
          event.key ===
          "Escape"
        ) {

          closeSuggestions();
        }

      }
    );



    // =======================================================
    // GAME DROPDOWN
    // =======================================================

    if (
      gameSelect
    ) {

      gameSelect.addEventListener(
        "change",

        function() {

          /*
            Abort autocomplete belonging to the
            previously selected game.
          */
          closeSuggestions();


          var selectedOption =
            getSelectedGameOption();


          if (
            selectedOption
          ) {

            var gameName =
              selectedOption.getAttribute(
                "data-game-name"
              ) ||
              selectedOption.textContent ||
              "";


            if (
              getGameType() ===
              ALL_SINGLES_TYPE
            ) {

              input.placeholder =
                "Search all singles...";

            } else {

              input.placeholder =
                "Search " +
                String(
                  gameName
                ).trim() +
                " singles...";

            }
          }


          input.focus();

        });
    }



    // =======================================================
    // FORM SUBMIT
    // =======================================================

    if (
      form
    ) {

      form.addEventListener(
        "submit",

        function(
          event
        ) {

          event.preventDefault();


          submitSearch(
            input.value
          );

        });
    }



    // =======================================================
    // CLOSE WHEN CLICKING OUTSIDE
    // =======================================================

    document.addEventListener(
      "click",

      function(
        event
      ) {

        if (
          !searchRoot.contains(
            event.target
          )
        ) {

          closeSuggestions();
        }

      }
    );



    // =======================================================
    // VISIBLE CUSTOMER QUERY
    // =======================================================

    /*
      Shopify's real q parameter contains our
      internal title/product_type syntax.

      singles_query contains only what the
      customer typed.
    */

    var currentUrl =
      new URL(
        window.location.href
      );


    var visibleQuery =
      currentUrl.searchParams.get(
        "singles_query"
      );


    var visibleQueryLabel =
      document.querySelector(
        ".singles-visible-query"
      );


    if (
      visibleQuery &&
      visibleQueryLabel
    ) {

      visibleQueryLabel.textContent =
        visibleQuery;
    }


    /*
      Preserve existing behavior:
      start the search box empty on a results page.
    */
    input.value =
      "";
  }



  // =========================================================
  // INITIALIZE EVERY SEARCH BAR ON THE PAGE
  // =========================================================

  function initializeAllSinglesSearchBars() {

    document

      .querySelectorAll(
        "[data-singles-search]"
      )

      .forEach(function(
        searchRoot
      ) {

        initializeSinglesSearch(
          searchRoot
        );

      });
  }


  if (
    document.readyState ===
    "loading"
  ) {

    document.addEventListener(
      "DOMContentLoaded",
      initializeAllSinglesSearchBars
    );

  } else {

    initializeAllSinglesSearchBars();
  }

})();